import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastViewport } from '../components/Toast';
import { AppShell } from './AppShell';
import { NotFoundPage } from './NotFoundPage';
import { RequireAuth, RequirePermission } from './guards';
import { useAuthStore } from './authStore';
import { LoginPage } from '../features/auth/LoginPage';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { HomePage } from '../features/home/HomePage';
import { BoardPage } from '../features/ideas/BoardPage';
import { IdeaNewPage } from '../features/ideas/IdeaNewPage';
import { IdeaDetailPage } from '../features/ideas/IdeaDetailPage';
import { MyIdeasPage } from '../features/ideas/MyIdeasPage';
import { StatsPage } from '../features/stats/StatsPage';
import { UsersPage } from '../features/admin/UsersPage';
import { SettingsPage } from '../features/admin/SettingsPage';
import { AuditPage } from '../features/admin/AuditPage';
import { UsagePage } from '../features/admin/UsagePage';

/**
 * 首次登录（或管理员重置口令后）强制改密。
 *
 * 初始口令写在环境变量里，属于「共享秘密」；不改掉就等于没有访问控制，
 * 因此这里不是提示而是硬闸门。
 */
function PasswordChangeGate({ children }: { children: ReactNode }) {
  const mustChange = useAuthStore((state) => state.mustChangePassword);
  const location = useLocation();

  if (mustChange && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace state={{ forced: true }} />;
  }
  return <>{children}</>;
}

export function App() {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          element={
            <RequireAuth>
              <PasswordChangeGate>
                <AppShell />
              </PasswordChangeGate>
            </RequireAuth>
          }
        >
          <Route path="/account/password" element={<ChangePasswordPage />} />

          <Route path="/" element={<HomePage />} />
          <Route path="/board" element={<BoardPage />} />
          <Route
            path="/ideas/new"
            element={
              <RequirePermission permissions={['idea.create']}>
                <IdeaNewPage />
              </RequirePermission>
            }
          />
          <Route path="/ideas/:id" element={<IdeaDetailPage />} />
          <Route path="/mine" element={<MyIdeasPage />} />
          <Route path="/stats" element={<StatsPage />} />

          <Route
            path="/admin/users"
            element={
              <RequirePermission permissions={['user.view']}>
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequirePermission permissions={['settings.manage']}>
                <SettingsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RequirePermission permissions={['audit.view']}>
                <AuditPage />
              </RequirePermission>
            }
          />

          <Route
            path="/admin/usage"
            element={
              <RequirePermission permissions={['usage.view']}>
                <UsagePage />
              </RequirePermission>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      <ToastViewport />
    </>
  );
}
